import React from 'react';
import { styled } from '@mui/system';
import TextField from '@mui/material/TextField';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import MenuItem from '@mui/material/MenuItem';
import { Formik, useFormik } from 'formik';
import { userSchema } from '../../../../schemas/user.request';
import { parse, format } from 'date-fns';
import useMediaQuery from '@mui/material/useMediaQuery';

const UserPanelContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '350px',
  minHeight: '660px',
  padding: '0px',
  [theme.breakpoints.down('md')]: {
    margin: '0 auto',
  },
}));
const SaveButton = styled('button')(({ theme }) => ({
  width: '60px',
  height: '30px',
  marginLeft: 'auto',
  background: '#AA9178',
  color: 'white',
  border: 'none',
  fontFamily: 'Fjalla One',
  fontStyle: 'normal',
  fontWeight: '400',
  fontSize: '16px',
  lineHeight: '20px',
  display: 'flex',
  alignItems: 'center',
  right: '50px',
  top: '5px',
  paddingLeft: '15px',
  '&:hover': {
    cursor: 'pointer',
  },
  [theme.breakpoints.down('md')]: {
    marginBottom: '25px',
    marginLeft: '0',
  },
}));
const UserPanelForm = styled('form')(({ theme }) => ({
  width: '100%',
  minHeight: '560px',
  [theme.breakpoints.down('md')]: {
    minHeight: '540px',
  },
}));

const TextInput = styled(TextField)({
  marginRight: '15px',
  marginTop: '5px',
  fontFamily: 'Raleway',
  fontStyle: 'normal',
  fontWeight: '400',
  fontSize: '11px',

  lineHeight: '13px',
  color: '#717171',

  '& .MuiInput-underline': {
    borderBottom: '1px solid rgb(166,146,123)',

    '&:hover::before': {
      borderBottom: '1px solid rgb(166,146,123)',
    },
  },

  '& ::placeholder': {
    color: 'rgba(153, 152, 154, 0.7)',
    fontFamily: 'Fjalla One',
    fontSize: '18px',
    fontWeight: '400',
    lineHeight: '23px',
    letterSpacing: '0em',
    textAlign: 'left',
  },
});

const TextInputProps = {
  sx: {
    color: '#000',
    fontFamily: 'Fjalla One',
    fontSize: '18px',
    fontWeight: '400',
    lineHeight: '23px',
    letterSpacing: '0em',
    textAlign: 'left',
    borderBottom: '2px solid #AA9178',
  },
};

const TextInputLabelProps = {
  sx: {
    // set the color of the label when not shrinked
    color: 'rgba(153, 152, 154, 0.7)',
    fontFamily: 'Fjalla One',
    fontSize: '18px',
    fontWeight: '400',
    letterSpacing: '0em',
    textAlign: 'left',

    '&.MuiInputLabel-shrink': {
      color: '#717171',
      fontFamily: 'Raleway',
      fontSize: '14px',
      top: '6px',
    },
  },
};

const DateInputLabelProps = {
  sx: {
    // set the color of the label when not shrinked
    color: 'rgba(153, 152, 154, 0.7)',
    fontFamily: 'Fjalla One',
    fontSize: '18px',
    fontWeight: '400',
    letterSpacing: '0em',
    textAlign: 'left',
    top: '-10px',
    left: '-14px',

    '&.MuiInputLabel-shrink': {
      color: '#717171',
      fontFamily: 'Raleway',
      fontSize: '14px',
      top: '6px',
    },
  },
};
const DOBPicker = styled(DatePicker)({
  marginTop: '20px',
  marginRight: '15px',

  color: 'rgba(153, 152, 154, 0.7)',
  '.MuiOutlinedInput-input': {
    padding: '10px 0 1px 0px',
  },

  '& input': {
    fontFamily: 'Fjalla One',
    fontWeight: '400',
    fontSize: '18px',
    lineHeight: '23px',
    color: 'black',
    left: '0',
    top: '0',
  },
  '& input::placeholder': {
    color: 'rgba(153, 152, 154, 0.7)',
  },
  '.MuiOutlinedInput-notchedOutline': {
    border: '0',
    borderRadius: '0',
    borderBottom: '2px solid #AA9178',
  },
});

const ButtonGroups = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  [theme.breakpoints.down('md')]: {
    marginBottom: '25px',
  },
}));
const SetButton = styled('a')({
  boxSizing: 'border-box',
  width: '150px',
  height: '30px',
  border: '2px solid #AA9178',
  marginTop: '22px',
  borderRadius: '15px',
  fontFamily: 'Fjalla One',
  fontStyle: 'normal',
  fontWeight: '400',
  fontSize: '16px',
  lineHeight: '20px',
  display: 'flex',
  color: '#000000',
  alignItems: 'center',
  paddingLeft: '25px',
  '&:hover': {
    cursor: 'pointer',
  },
  textDecoration: 'none',
});
const ErrorMessage = styled('div')({
  fontSize: '12px',
  color: 'red',
  height: '5px',
});
const ErrorMessageEmpty = styled('div')({ height: '5px' });
const AccountNameContainer = styled('div')({ margin: '0', padding: '0' });
const EmailContainer = styled('div')({ margin: '0', padding: '0' });
const FirstNameContainer = styled('div')({ margin: '0', padding: '0' });
const LastNameContainer = styled('div')({ margin: '0', padding: '0' });
const NumberContainer = styled('div')({ margin: '0', padding: '0' });
const DOBContainer = styled('div')({ margin: '0', padding: '0' });
const GenderContainer = styled('div')({ margin: '0', padding: '0' });
const PersonInfoContainer = styled('div')({ margin: '0', padding: '0' });
const UnitContainer = styled('div')({ margin: '0', padding: '0' });
const StreetContainer = styled('div')({ margin: '0', padding: '0' });
const SuburbContainer = styled('div')({ margin: '0', padding: '0' });
const StateContainer = styled('div')({ margin: '0', padding: '0' });
const OneLineContainer = styled('div')({
  display: 'flex',
  flexDirection: 'row',
});

const genders = [
  {
    label: 'Female',
    key: 'F',
  },
  {
    label: 'Male',
    key: 'M',
  },
  {
    label: 'Other',
    key: 'O',
  },
];
const states = [
  {
    label: 'New South Wales',
    key: 'NSW',
  },
  {
    label: 'Victoria',
    key: 'VIC',
  },
  {
    label: 'Queensland',
    key: 'QLD',
  },
  {
    label: 'South Australia',
    key: 'SA',
  },
  {
    label: 'Western Australia',
    key: 'WA',
  },
  {
    label: 'Tasmania',
    key: 'TAS',
  },
  {
    label: 'Northern Territory',
    key: 'NT',
  },
  {
    label: 'Australia Capital Territory',
    key: 'ACT',
  },
];

const validate = (values) => {
  const errors = {};
  const valid = userSchema.validate(values, { abortEarly: false });
  if (valid.error) {
    valid.error.details.forEach((err) => {
      errors[err.path.join('.')] = err.message;
    });
  }
  return errors;
};

const UserSettingPanel = () => {
  const formik = useFormik({
    initialValues: {
      userName: 'Hello',
      firstName: '',
      lastName: '',
      email: 'Hello@JR.com',
      DOB: '',
      gender: '',
      mobilePhone: '',
      personalIntro: '',
      unit: '',
      street: '',
      suburb: '',
      state: '',
    },
    validate,
    onSubmit: (values) => {
      alert(JSON.stringify(values, null, 2));
    },
  });

  const [gender, setGender] = React.useState(null);
  const [state, setState] = React.useState(null);
  const handleChange = (event) => {
    setGender(event.target.value);
    setState(event.target.value);
  };

  const mobileMatch = useMediaQuery((theme) => theme.breakpoints.down('md'));

  return (
    <UserPanelContainer>
      <UserPanelForm autoComplete="off" onSubmit={formik.handleSubmit}>
        {mobileMatch ? null : <SaveButton type="submit">Save</SaveButton>}
        <AccountNameContainer>
          <TextInput
            disabled
            multiline
            sx={{ width: '350px' }}
            label="Account Name"
            variant="standard"
            id="userName"
            type="text"
            InputProps={{ ...TextInputProps, disableUnderline: true }}
            InputLabelProps={TextInputLabelProps}
            onChange={formik.handleChange}
            value={formik.values.userName}
          />
        </AccountNameContainer>
        <EmailContainer>
          <TextInput
            disabled
            id="email"
            multiline
            sx={{ width: '350px' }}
            label="Email"
            variant="standard"
            type="email"
            onChange={formik.handleChange}
            value={formik.values.email}
            InputProps={{ ...TextInputProps, disableUnderline: true }}
            InputLabelProps={TextInputLabelProps}
          />
        </EmailContainer>
        <OneLineContainer>
          <FirstNameContainer>
            <TextInput
              sx={{ width: '167px', marginTop: '2px' }}
              id="firstName"
              label="First Name"
              variant="standard"
              placeholder="Add First Name"
              type="text"
              InputProps={{ ...TextInputProps, disableUnderline: true }}
              InputLabelProps={TextInputLabelProps}
              onChange={formik.handleChange}
              value={formik.values.firstName}
            />
            {formik.errors.firstName && formik.touched.firstName ? (
              <ErrorMessage>invalid First Name</ErrorMessage>
            ) : (
              <ErrorMessageEmpty />
            )}
          </FirstNameContainer>
          <LastNameContainer>
            <TextInput
              multiline
              sx={{ width: '168px' }}
              label="Last Name"
              id="lastName"
              variant="standard"
              placeholder="Add Last Name"
              type="text"
              InputProps={{ ...TextInputProps, disableUnderline: true }}
              InputLabelProps={TextInputLabelProps}
              onChange={formik.handleChange}
              value={formik.values.lastName}
            />
            {formik.errors.lastName && formik.touched.lastName ? (
              <ErrorMessage>invalid Last Name</ErrorMessage>
            ) : (
              <ErrorMessageEmpty />
            )}
          </LastNameContainer>
        </OneLineContainer>
        <NumberContainer>
          <TextInput
            multiline
            sx={{ width: '350px' }}
            label="Number"
            id="mobilePhone"
            variant="standard"
            placeholder="Add your number"
            type="number"
            InputProps={{ ...TextInputProps, disableUnderline: true }}
            InputLabelProps={TextInputLabelProps}
            onChange={formik.handleChange}
            value={formik.values.mobilePhone}
          />
          {formik.errors.mobilePhone && formik.touched.mobilePhone ? (
            <ErrorMessage>invalid phone number</ErrorMessage>
          ) : (
            <ErrorMessageEmpty />
          )}
        </NumberContainer>
        <OneLineContainer>
          <DOBContainer>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DOBPicker
                label="Birthday"
                name="DOB"
                placeholder="Add your birthday"
                sx={{ width: '167px' }}
                id="DOB"
                value={formik.values.DOB || null}
                onChange={(value) => {
                  const formattedValue = format(new Date(value), 'dd/MM/yyyy');
                  formik.setFieldValue('DOB', formattedValue);
                }}
                renderInput={(params) => (
                  <TextField
                    sx={{ alignItems: 'center' }}
                    InputLabelProps={DateInputLabelProps}
                    {...params}
                  />
                )}
              ></DOBPicker>
              {formik.errors.DOB && formik.touched.DOB ? (
                <ErrorMessage>required</ErrorMessage>
              ) : (
                <ErrorMessageEmpty />
              )}
            </LocalizationProvider>
          </DOBContainer>
          <GenderContainer>
            <TextInput
              select
              label="Gender"
              sx={{ width: '168px' }}
              type="text"
              id="gender"
              name="gender"
              value={formik.values.gender}
              onChange={formik.handleChange}
              variant="standard"
              InputLabelProps={TextInputLabelProps}
              InputProps={{ ...TextInputProps, disableUnderline: true }}
            >
              {genders.map((option) => (
                <MenuItem value={option.label} key={option.label}>
                  {option.label}
                </MenuItem>
              ))}
            </TextInput>
            {formik.errors.gender && formik.touched.gender ? (
              <ErrorMessage>required</ErrorMessage>
            ) : (
              <ErrorMessageEmpty />
            )}
          </GenderContainer>
        </OneLineContainer>
        <PersonInfoContainer>
          <TextInput
            multiline
            sx={{ width: '350px' }}
            label="Bio"
            id="personalIntro"
            rows={3}
            variant="standard"
            type="text"
            onChange={formik.handleChange}
            value={formik.values.personalIntro}
            InputProps={{ ...TextInputProps, disableUnderline: true }}
            InputLabelProps={TextInputLabelProps}
            placeholder="Write some words"
          />
          {formik.errors.personalIntro && formik.touched.personalIntro ? (
            <ErrorMessage>please input your introduction</ErrorMessage>
          ) : (
            <ErrorMessageEmpty />
          )}
        </PersonInfoContainer>
        <OneLineContainer>
          <UnitContainer>
            <TextInput
              multiline
              id="unit"
              sx={{ width: '167px' }}
              label="Street/Unit Number"
              type="text"
              onChange={formik.handleChange}
              value={formik.values.unit}
              variant="standard"
              InputProps={{ ...TextInputProps, disableUnderline: true }}
              InputLabelProps={TextInputLabelProps}
            />
            {formik.errors.unit && formik.touched.unit ? (
              <ErrorMessage>required</ErrorMessage>
            ) : (
              <ErrorMessageEmpty />
            )}
          </UnitContainer>
          <StreetContainer>
            <TextInput
              multiline
              id="street"
              sx={{ width: '168px' }}
              label="Street"
              type="text"
              variant="standard"
              onChange={formik.handleChange}
              value={formik.values.street}
              InputProps={{ ...TextInputProps, disableUnderline: true }}
              InputLabelProps={TextInputLabelProps}
            />
            {formik.errors.street && formik.touched.street ? (
              <ErrorMessage>invalid street name</ErrorMessage>
            ) : (
              <ErrorMessageEmpty />
            )}
          </StreetContainer>
        </OneLineContainer>
        <OneLineContainer>
          <SuburbContainer>
            <TextInput
              multiline
              id="suburb"
              sx={{ width: '167px', marginTop: '8px' }}
              label="Suburb"
              type="text"
              variant="standard"
              onChange={formik.handleChange}
              value={formik.values.suburb}
              InputProps={{ ...TextInputProps, disableUnderline: true }}
              InputLabelProps={TextInputLabelProps}
            />
            {formik.errors.suburb && formik.touched.suburb ? (
              <ErrorMessage>required</ErrorMessage>
            ) : (
              <ErrorMessageEmpty />
            )}
          </SuburbContainer>

          <StateContainer>
            <TextInput
              select
              label="State"
              id="state"
              name="state"
              sx={{ width: '168px' }}
              type="text"
              value={formik.values.state}
              onChange={(handleChange, formik.handleChange)}
              variant="standard"
              InputProps={{ ...TextInputProps, disableUnderline: true }}
              InputLabelProps={TextInputLabelProps}
            >
              {states.map((option) => (
                <MenuItem value={option.label} key={option.label}>
                  {option.label}
                </MenuItem>
              ))}
            </TextInput>
            {formik.errors.state && formik.touched.state ? (
              <ErrorMessage>required</ErrorMessage>
            ) : (
              <ErrorMessageEmpty />
            )}
          </StateContainer>
        </OneLineContainer>
      </UserPanelForm>

      <ButtonGroups>
        <SetButton href="/reset">Reset Password</SetButton>
        <SetButton sx={{ marginLeft: '30px' }}>Edit Followings</SetButton>
      </ButtonGroups>

      {mobileMatch ? <SaveButton type="submit">Save</SaveButton> : null}
    </UserPanelContainer>
  );
};
export default UserSettingPanel;
